import type { PublicGuidebookPayload, GuidebookPlace, GuidebookHighlight, GuidebookHouseRules, GuidebookWeather, TvSlideConfig } from "../guidebook/types"
import type { TvSlide } from "./useSlideEngine"
import { windDirectionLabel, formatCheckoutDay, buildWeatherOneliner, resolveAreaName, truncate } from "./tvUtils"

export type TvDisplayMode = "standalone" | "complement" | "ambient"

// ---------------------------------------------------------------------------
// Legacy config type → new scene type mapping (backwards compat)
// ---------------------------------------------------------------------------

const LEGACY_TO_SCENE: Record<string, string> = {
  welcome: "hero",
  photo: "hero",
  weather: "today",
  wifi: "essentials",
  rules: "essentials",
  places: "curated_place",
}

// ---------------------------------------------------------------------------
// Behavioural intelligence context
// ---------------------------------------------------------------------------

interface SlideContext {
  hour: number
  precipitation: number
  windSpeed: number
  bookingPhase: string
  isLastDay: boolean
}

function resolveSlideContext(data: PublicGuidebookPayload): SlideContext {
  let hour = new Date().getHours()
  const tz = data.weather?.timezone
  if (tz) {
    try {
      const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "numeric", hour12: false }).formatToParts(new Date())
      const hPart = parts.find((p) => p.type === "hour")
      if (hPart) hour = parseInt(hPart.value, 10)
    } catch { /* fallback to local */ }
  }

  const phase = data.tv_state?.booking_phase || "during_stay"

  return {
    hour,
    precipitation: data.weather?.precipitation_mm ?? 0,
    windSpeed: data.weather?.wind_speed_ms ?? 0,
    bookingPhase: phase,
    isLastDay: phase === "checkout" || phase === "last_evening",
  }
}

// ---------------------------------------------------------------------------
// Weather narrative
// ---------------------------------------------------------------------------

function buildWeatherNarrative(weather: GuidebookWeather): string {
  const temp = Math.round(weather.temperature_c ?? 0)
  const label = (weather.weather_label || "").toLowerCase()
  const windDir = windDirectionLabel(weather.wind_direction_deg)
  const windSpeed = Math.round(weather.wind_speed_ms ?? 0)

  let narrative = `${temp}\u00B0, ${label}.`

  if (weather.precipitation_mm && weather.precipitation_mm > 0) {
    narrative += " A good day for indoor discoveries."
  } else if (windSpeed > 6 && windDir) {
    narrative += ` Brisk ${windDir} winds at ${windSpeed} m/s.`
  } else if (weather.weather_code === 0 || weather.weather_code === 1) {
    narrative += " Clear skies and calm conditions."
  }

  return narrative
}

// ---------------------------------------------------------------------------
// Highlight selection — time-aware
// ---------------------------------------------------------------------------

const SLOT_BY_TIME: Record<string, string[]> = {
  morning: ["morning_coffee", "beach_now", "activity_now"],
  midday: ["lunch", "beach_now", "activity_now"],
  afternoon: ["activity_now", "beach_now", "dinner"],
  evening: ["dinner", "activity_now", "tomorrow_plan"],
}

function getTimeSlot(hour: number): string {
  if (hour >= 6 && hour < 11) return "morning"
  if (hour >= 11 && hour < 14) return "midday"
  if (hour >= 14 && hour < 17) return "afternoon"
  return "evening"
}

function pickTimeAwareHighlights(
  highlights: GuidebookHighlight[],
  places: GuidebookPlace[],
  ctx: SlideContext,
): Array<{ emoji: string; label: string; motivation: string }> {
  const timeSlot = getTimeSlot(ctx.hour)
  const preferredSlots = SLOT_BY_TIME[timeSlot] || SLOT_BY_TIME.evening

  const sorted = [...highlights].sort((a, b) => {
    const aIdx = preferredSlots.indexOf(a.slot)
    const bIdx = preferredSlots.indexOf(b.slot)
    const aPri = aIdx >= 0 ? aIdx : 99
    const bPri = bIdx >= 0 ? bIdx : 99
    return aPri - bPri
  })

  const result: Array<{ emoji: string; label: string; motivation: string }> = []
  for (const h of sorted) {
    if (result.length >= 3) break
    const place = places.find((p) => p.id === h.place_id)
    let motivation = h.motivation
    if (ctx.precipitation > 0 && place?.en?.rainAlternative) {
      motivation = place.en.rainAlternative
    }
    result.push({ emoji: h.emoji, label: h.label, motivation })
  }

  return result
}

// ---------------------------------------------------------------------------
// Place selection — context-aware sorting
// ---------------------------------------------------------------------------

const INDOOR_TYPES = new Set(["restaurant", "cafe", "museum", "gallery", "spa", "shop", "bar"])
const OUTDOOR_TYPES = new Set(["beach", "park", "trail", "viewpoint"])

function sortPlacesByContext(places: GuidebookPlace[], ctx: SlideContext): GuidebookPlace[] {
  return [...places].sort((a, b) => {
    let aScore = 0
    let bScore = 0

    if (ctx.hour >= 6 && ctx.hour < 11) {
      if (a.type === "cafe") aScore -= 2
      if (b.type === "cafe") bScore -= 2
    } else if (ctx.hour >= 11 && ctx.hour < 14) {
      if (a.type === "restaurant") aScore -= 2
      if (b.type === "restaurant") bScore -= 2
    } else if (ctx.hour >= 17) {
      if (a.type === "restaurant" || a.type === "bar") aScore -= 2
      if (b.type === "restaurant" || b.type === "bar") bScore -= 2
    }

    if (ctx.precipitation > 0) {
      if (a.type && INDOOR_TYPES.has(a.type)) aScore -= 1
      if (b.type && INDOOR_TYPES.has(b.type)) bScore -= 1
      if (a.type && OUTDOOR_TYPES.has(a.type)) aScore += 2
      if (b.type && OUTDOOR_TYPES.has(b.type)) bScore += 2
    }

    if (ctx.windSpeed > 8) {
      if (a.type === "beach") aScore += 3
      if (b.type === "beach") bScore += 3
    }

    return aScore - bScore
  })
}

// ---------------------------------------------------------------------------
// Pick top places — prefer highlighted, then diverse categories
// ---------------------------------------------------------------------------

function pickTopPlaces(
  places: GuidebookPlace[],
  highlights?: GuidebookHighlight[],
  max: number = 3,
): GuidebookPlace[] {
  const result: GuidebookPlace[] = []
  const usedIds = new Set<string>()

  if (highlights) {
    for (const h of highlights) {
      if (result.length >= max) break
      const place = places.find((p) => p.id === h.place_id)
      if (place && !usedIds.has(place.id)) {
        result.push(place)
        usedIds.add(place.id)
      }
    }
  }

  const categoryOrder = ["restaurant", "beach", "cafe", "attraction", "park", "shop"]
  for (const cat of categoryOrder) {
    if (result.length >= max) break
    const place = places.find((p) => p.type === cat && !usedIds.has(p.id))
    if (place) {
      result.push(place)
      usedIds.add(place.id)
    }
  }

  if (result.length < max) {
    for (const place of places) {
      if (result.length >= max) break
      if (!usedIds.has(place.id)) {
        result.push(place)
        usedIds.add(place.id)
      }
    }
  }

  return result
}

// ---------------------------------------------------------------------------
// Essentials data builder
// ---------------------------------------------------------------------------

function buildEssentials(data: PublicGuidebookPayload): Record<string, unknown> | null {
  const rules = data.houseRules
  const hasWifi = Boolean(data.property.wifi_name)
  const hasRules = rules && hasAnyDisplayRules(rules)

  if (!hasWifi && !hasRules) return null

  const quietHoursLine =
    rules?.quietHours?.enabled && rules.quietHours.startTime
      ? `${rules.quietHours.startTime} \u2013 ${rules.quietHours.endTime || "07:00"}`
      : null

  const compactRules: string[] = []
  if (rules?.smoking?.enabled) compactRules.push(rules.smoking.value || "No smoking")
  if (rules?.pets?.enabled) compactRules.push(rules.pets.value || "No pets")
  if (rules?.parties?.enabled) compactRules.push(rules.parties.value || "No parties")

  return {
    wifiName: data.property.wifi_name || null,
    wifiPassword: data.property.wifi_password || null,
    checkoutTime: rules?.checkout?.enabled ? rules.checkout.checkoutTime || null : null,
    quietHoursLine,
    compactRules: compactRules.slice(0, 3),
  }
}

function hasAnyDisplayRules(rules: GuidebookHouseRules): boolean {
  return Boolean(
    rules.quietHours?.enabled ||
    rules.smoking?.enabled ||
    rules.pets?.enabled ||
    rules.parties?.enabled ||
    rules.checkout?.enabled,
  )
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

export function buildSlides(
  data: PublicGuidebookPayload,
  mode: TvDisplayMode,
  config?: TvSlideConfig[] | null,
): TvSlide[] {
  const isBetween = data.tv_state?.booking_phase === "between_bookings"

  let slides: TvSlide[]

  if (isBetween) {
    slides = buildBetweenBookingsSlides(data)
  } else {
    switch (mode) {
      case "complement":
        slides = buildComplementSlides(data)
        break
      case "ambient":
        slides = buildAmbientSlides(data)
        break
      default:
        slides = buildStandaloneSlides(data)
        break
    }
  }

  if (mode === "standalone" && config && config.length > 0) {
    slides = applySlideConfig(slides, config)
  }

  return slides
}

// ---------------------------------------------------------------------------
// Between-bookings — minimal hero only
// ---------------------------------------------------------------------------

function buildBetweenBookingsSlides(data: PublicGuidebookPayload): TvSlide[] {
  return [{
    id: "hero",
    type: "hero",
    data: {
      coverImageUrl: data.property.cover_image_url || null,
      propertyName: data.property.name,
      guestName: null,
      checkoutDayOfWeek: null,
      checkinTime: null,
      checkoutTime: null,
      wifiName: null,
      weatherLine: buildWeatherOneliner(data.weather),
      bookingPhase: "between_bookings",
    },
    duration: 60,
  }]
}

// ---------------------------------------------------------------------------
// Standalone — full cinematic experience
// ---------------------------------------------------------------------------

function buildStandaloneSlides(data: PublicGuidebookPayload): TvSlide[] {
  const slides: TvSlide[] = []
  const ctx = resolveSlideContext(data)

  // 1. Hero Welcome — always first, longest
  slides.push({
    id: "hero",
    type: "hero",
    data: {
      coverImageUrl: data.property.cover_image_url || null,
      propertyName: data.property.name,
      guestName: data.tv_state?.guest_name || null,
      checkoutDayOfWeek: formatCheckoutDay(data.tv_state?.check_out),
      checkinTime: data.property.checkin_time || null,
      checkoutTime: data.houseRules?.checkout?.checkoutTime || null,
      wifiName: data.property.wifi_name || null,
      weatherLine: buildWeatherOneliner(data.weather),
      bookingPhase: data.tv_state?.booking_phase || null,
    },
    duration: 50,
  })

  // 2. Today in {area} — needs weather + highlights
  if (data.weather && data.highlights && data.highlights.length > 0) {
    const areaName = resolveAreaName(data.property, data.places)
    const timeFilteredHighlights = pickTimeAwareHighlights(data.highlights, data.places, ctx)

    if (timeFilteredHighlights.length > 0) {
      slides.push({
        id: "today",
        type: "today",
        data: {
          areaName,
          weatherNarrative: buildWeatherNarrative(data.weather),
          highlights: timeFilteredHighlights,
        },
        duration: 30,
      })
    }
  }

  // 3. Curated places — 2-3 rotating, context-sorted
  const topPlaces = pickTopPlaces(data.places, data.highlights, 3)
  const sortedPlaces = sortPlacesByContext(topPlaces, ctx)
  for (const place of sortedPlaces) {
    const copy = place.en
    if (!copy) continue
    slides.push({
      id: `place-${place.id}`,
      type: "curated_place",
      data: {
        name: copy.name,
        shortDescription: truncate(copy.short || copy.why || null, 120),
        tip: copy.tip || null,
      },
      duration: 22,
    })
  }

  // 4. House Essentials — merged wifi + rules
  const essentials = buildEssentials(data)
  if (essentials) {
    if (ctx.isLastDay) {
      // On last day, show essentials right after hero
      slides.splice(1, 0, {
        id: "essentials",
        type: "essentials",
        data: essentials,
        duration: 18,
      })
    } else {
      slides.push({
        id: "essentials",
        type: "essentials",
        data: essentials,
        duration: 15,
      })
    }
  }

  return slides
}

// ---------------------------------------------------------------------------
// Complement — visual emphasis (paired with e-ink)
// ---------------------------------------------------------------------------

function buildComplementSlides(data: PublicGuidebookPayload): TvSlide[] {
  const slides: TvSlide[] = []
  const ctx = resolveSlideContext(data)

  slides.push({
    id: "hero",
    type: "hero",
    data: {
      coverImageUrl: data.property.cover_image_url || null,
      propertyName: data.property.name,
      guestName: data.tv_state?.guest_name || null,
      checkoutDayOfWeek: formatCheckoutDay(data.tv_state?.check_out),
      checkinTime: null,
      checkoutTime: null,
      wifiName: null,
      weatherLine: buildWeatherOneliner(data.weather),
      bookingPhase: data.tv_state?.booking_phase || null,
    },
    duration: 40,
  })

  if (data.weather && data.highlights && data.highlights.length > 0) {
    const areaName = resolveAreaName(data.property, data.places)
    const highlights = pickTimeAwareHighlights(data.highlights, data.places, ctx)
    if (highlights.length > 0) {
      slides.push({
        id: "today",
        type: "today",
        data: {
          areaName,
          weatherNarrative: buildWeatherNarrative(data.weather),
          highlights,
        },
        duration: 25,
      })
    }
  }

  const topPlaces = pickTopPlaces(data.places, data.highlights, 4)
  const sortedPlaces = sortPlacesByContext(topPlaces, ctx)
  for (const place of sortedPlaces) {
    const copy = place.en
    if (!copy) continue
    slides.push({
      id: `place-${place.id}`,
      type: "curated_place",
      data: {
        name: copy.name,
        shortDescription: truncate(copy.short || copy.why || null, 120),
        tip: copy.tip || null,
      },
      duration: 20,
    })
  }

  if (slides.length === 0) {
    slides.push({
      id: "hero-fallback",
      type: "hero",
      data: {
        coverImageUrl: null,
        propertyName: data.property.name,
        guestName: null,
        checkoutDayOfWeek: null,
        checkinTime: null,
        checkoutTime: null,
        wifiName: null,
        weatherLine: buildWeatherOneliner(data.weather),
        bookingPhase: data.tv_state?.booking_phase || null,
      },
      duration: 60,
    })
  }

  return slides
}

// ---------------------------------------------------------------------------
// Ambient — screensaver mode
// ---------------------------------------------------------------------------

function buildAmbientSlides(data: PublicGuidebookPayload): TvSlide[] {
  return [{
    id: "hero-ambient",
    type: "hero",
    data: {
      coverImageUrl: data.property.cover_image_url || null,
      propertyName: data.property.name,
      guestName: null,
      checkoutDayOfWeek: null,
      checkinTime: null,
      checkoutTime: null,
      wifiName: null,
      weatherLine: buildWeatherOneliner(data.weather),
      bookingPhase: "between_bookings",
    },
    duration: 60,
  }]
}

// ---------------------------------------------------------------------------
// Host config post-processing (backwards compat)
// ---------------------------------------------------------------------------

function applySlideConfig(slides: TvSlide[], config: TvSlideConfig[]): TvSlide[] {
  const configMap = new Map<string, { enabled: boolean; duration: number; order: number }>()

  for (const entry of config) {
    const sceneType = LEGACY_TO_SCENE[entry.type] || entry.type
    const existing = configMap.get(sceneType)

    if (existing) {
      if (!entry.enabled) existing.enabled = false
      if (entry.duration > 0 && entry.duration < existing.duration) {
        existing.duration = entry.duration
      }
      if (entry.order < existing.order) {
        existing.order = entry.order
      }
    } else {
      configMap.set(sceneType, {
        enabled: entry.enabled,
        duration: entry.duration,
        order: entry.order,
      })
    }
  }

  const filtered = slides.filter((slide) => {
    const entry = configMap.get(slide.type)
    if (!entry) return true
    return entry.enabled
  })

  filtered.sort((a, b) => {
    const orderA = configMap.get(a.type)?.order ?? 999
    const orderB = configMap.get(b.type)?.order ?? 999
    return orderA - orderB
  })

  return filtered.map((slide) => {
    const entry = configMap.get(slide.type)
    if (entry && entry.duration > 0) {
      return { ...slide, duration: entry.duration }
    }
    return slide
  })
}
